/**
 * Admin Commissions — Fase 3 sistema agenti.
 * Tabella provvigioni con filtri, ricalcolo massivo, export CSV, mark-paid.
 */
import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import {
  RefreshCw, Download, CheckCircle2, XCircle, Trash2, Filter, Wallet,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'In attesa', color: 'bg-gray-100 text-gray-700' },
  accrued: { label: 'Maturata', color: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Pagata', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annullata', color: 'bg-red-50 text-red-700' },
};

const AdminCommissions = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState({});
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [agentFilter, setAgentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const auth = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agentFilter) params.set('agent_id', agentFilter);
      if (statusFilter) params.set('status', statusFilter);
      const [cRes, aRes] = await Promise.all([
        fetch(`${API}/api/admin/commissions?${params}`, { headers: auth() }),
        fetch(`${API}/api/admin/agents`, { headers: auth() }),
      ]);
      if (cRes.ok) {
        const j = await cRes.json();
        setCommissions(j.data || []);
        setTotals(j.totals || {});
      }
      if (aRes.ok) setAgents((await aRes.json()).data || []);
    } catch (e) {
      toast({ title: 'Errore caricamento', description: String(e.message), variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken, agentFilter, statusFilter]);

  const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  const recompute = async () => {
    setRecomputing(true);
    try {
      const res = await fetch(`${API}/api/admin/commissions/recompute`, { method: 'POST', headers: auth() });
      const j = await res.json();
      if (res.ok) {
        toast({ title: 'Ricalcolo completato', description: j.message });
        load();
      } else {
        toast({ title: 'Errore', description: j.detail || 'Errore ricalcolo', variant: 'destructive' });
      }
    } finally { setRecomputing(false); }
  };

  const markPaid = async (c) => {
    if (!window.confirm(`Confermi il pagamento di ${eur(c.amount)} a ${c.agentName || c.agentId}?`)) return;
    const res = await fetch(`${API}/api/admin/commissions/${c.id}/mark-paid`, { method: 'POST', headers: auth() });
    if (res.ok) { toast({ title: 'Contrassegnata come pagata' }); load(); }
    else toast({ title: 'Errore', variant: 'destructive' });
  };

  const cancelComm = async (c) => {
    if (!window.confirm('Annullare questa commissione?')) return;
    const res = await fetch(`${API}/api/admin/commissions/${c.id}`, {
      method: 'PUT', headers: auth(), body: JSON.stringify({ status: 'cancelled' }),
    });
    if (res.ok) { toast({ title: 'Commissione annullata' }); load(); }
    else toast({ title: 'Errore', variant: 'destructive' });
  };

  const deleteComm = async (c) => {
    if (!window.confirm('Eliminare definitivamente questa commissione?')) return;
    const res = await fetch(`${API}/api/admin/commissions/${c.id}`, { method: 'DELETE', headers: auth() });
    if (res.ok) { toast({ title: 'Eliminata' }); load(); }
    else toast({ title: 'Errore', variant: 'destructive' });
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (agentFilter) params.set('agent_id', agentFilter);
    if (statusFilter) params.set('status', statusFilter);
    // fetch as blob (need auth header); usiamo fetch → blob
    fetch(`${API}/api/admin/commissions/export.csv?${params}`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `commissioni_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  const agentOptions = useMemo(() => agents.map((a) => ({ id: a.id, label: `${a.firstName} ${a.lastName} (${a.code || a.email})` })), [agents]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Provvigioni</h1>
            <p className="mt-1 text-gray-600">Calcola, gestisci e paga le commissioni degli agenti.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={recompute} disabled={recomputing} data-testid="btn-recompute"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'Ricalcolo…' : 'Ricalcola tutte'}
            </button>
            <button onClick={exportCsv} data-testid="btn-export-csv"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['pending', 'accrued', 'paid', 'cancelled'].map((s) => (
            <div key={s} className="bg-white rounded-2xl border p-4">
              <div className="text-xs uppercase text-gray-500 font-semibold">{STATUS_LABEL[s].label}</div>
              <div className="mt-1 text-xl font-bold tabular-nums">{eur(totals[s])}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}
              data-testid="filter-agent"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="">Tutti gli agenti</option>
              {agentOptions.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="filter-status"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="">Tutti gli stati</option>
              <option value="pending">In attesa</option>
              <option value="accrued">Maturata</option>
              <option value="paid">Pagata</option>
              <option value="cancelled">Annullata</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center"><LoadingSpinner /></div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {commissions.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                <Wallet className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                Nessuna commissione. Assegna clienti agli agenti e ricalcola.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-purple-50/60 text-purple-700 uppercase text-[10px]">
                  <tr>
                    <th className="text-left px-4 py-3">Ordine</th>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Agente</th>
                    <th className="text-right px-4 py-3">Totale ordine</th>
                    <th className="text-right px-4 py-3">%</th>
                    <th className="text-right px-4 py-3">Importo</th>
                    <th className="text-center px-4 py-3">Stato</th>
                    <th className="text-right px-4 py-3">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {commissions.map((c) => {
                    const s = STATUS_LABEL[c.status] || { label: c.status, color: 'bg-gray-100' };
                    return (
                      <tr key={c.id} className="hover:bg-purple-50/40" data-testid={`admin-commission-row-${c.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-purple-700">{c.orderNumber}</td>
                        <td className="px-4 py-3">{c.customerName}</td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{c.agentName || c.agentId}</div>
                          <div className="text-[10px] text-gray-500">
                            {c.agentCode} {c.role === 'parent-of-sub' && <span className="ml-1 px-1 rounded bg-blue-100 text-blue-700">split parent {c.parentSplit}%</span>}
                            {c.role === 'sub-agent' && <span className="ml-1 px-1 rounded bg-purple-100 text-purple-700">SUB</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{eur(c.orderTotal)}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs">{c.rate}%</td>
                        <td className="px-4 py-3 text-right font-semibold text-purple-700 tabular-nums">{eur(c.amount)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>
                            {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            {c.status !== 'paid' && c.status !== 'cancelled' && (
                              <button onClick={() => markPaid(c)} title="Segna come pagata"
                                data-testid={`mark-paid-${c.id}`}
                                className="p-1.5 rounded hover:bg-green-50">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            {c.status !== 'cancelled' && (
                              <button onClick={() => cancelComm(c)} title="Annulla"
                                data-testid={`cancel-${c.id}`}
                                className="p-1.5 rounded hover:bg-amber-50">
                                <XCircle className="w-4 h-4 text-amber-600" />
                              </button>
                            )}
                            <button onClick={() => deleteComm(c)} title="Elimina"
                              data-testid={`delete-${c.id}`}
                              className="p-1.5 rounded hover:bg-red-50">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCommissions;
